<template>
  <div class="reports-filters-container">
    <template v-if="!mdAndUp">
      <v-expansion-panels
        v-model="mobileExpandedPanel"
        class="reports-filter-panels"
        elevation="0"
      >
        <v-expansion-panel class="reports-filter-section" elevation="0">
          <v-expansion-panel-title class="pa-0">
            <div class="d-flex justify-space-between align-center py-1 w-100">
              <h5 class="text-secondary font-weight-bold">More Filters</h5>
            </div>
          </v-expansion-panel-title>
          <v-divider v-if="mobileExpandedPanel === 0" class="mt-3"></v-divider>
          <v-expansion-panel-text class="reports-filter-content">
            <v-expansion-panels
              v-model="expandedPanels"
              multiple
              class="reports-filter-panels"
              elevation="0"
            >
              <template v-if="hasFilters">
                <template
                  v-for="(filter, index) in reportsStore.filters"
                  :key="filter.id"
                >
                  <v-expansion-panel
                    class="reports-filter-section"
                    elevation="0"
                  >
                    <v-expansion-panel-title class="pa-0">
                      <div
                        class="d-flex justify-space-between align-center py-3 w-100"
                      >
                        <div class="section-title">{{ filter.name }}</div>
                      </div>
                    </v-expansion-panel-title>
                    <v-expansion-panel-text class="reports-filter-content">
                      <v-checkbox
                        v-for="option in getSortedOptions(filter)"
                        :key="option.value"
                        :model-value="
                          option.value === 'any'
                            ? isAnySelected(filter)
                            : isOptionSelected(filter, option)
                        "
                        :label="option.label"
                        density="compact"
                        color="primary"
                        hide-details
                        class="bordered-checkbox"
                        @update:model-value="
                          (val: boolean | null) => {
                            if (option.value === 'any') {
                              handleAnySelection(filter, val ?? false);
                            } else {
                              handleFilterInteraction(
                                filter.id,
                                option.value,
                                val ?? false,
                              );
                            }
                          }
                        "
                      ></v-checkbox>
                      <!-- Dynamic filter description with HTML support -->
                      <div
                        v-if="filter.description"
                        class="filter-description pt-2"
                        v-html="filter.description"
                      ></div>
                    </v-expansion-panel-text>
                  </v-expansion-panel>
                  <v-divider
                    v-if="index < reportsStore.filters.length - 1"
                  ></v-divider>
                </template>
              </template>
              <template v-else>
                <div
                  v-if="!isLoading && !reportsStore.error && !hasFilters"
                  class="text-body-2 pa-4"
                >
                  No filters available
                </div>
                <div v-else-if="reportsStore.error" class="text-error pa-4">
                  {{ reportsStore.error }}
                </div>
                <div v-else class="pa-4 text-grey">Loading filters...</div>
              </template>
            </v-expansion-panels>
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>
    </template>
    <template v-else>
      <v-expansion-panels
        v-model="expandedPanels"
        multiple
        class="reports-filter-panels"
        elevation="0"
      >
        <template v-if="hasFilters">
          <template
            v-for="(filter, index) in reportsStore.filters"
            :key="filter.id"
          >
            <v-expansion-panel class="reports-filter-section" elevation="0">
              <v-expansion-panel-title class="pa-0">
                <div
                  class="d-flex justify-space-between align-center py-3 w-100"
                >
                  <div class="section-title">{{ filter.name }}</div>
                </div>
              </v-expansion-panel-title>
              <v-expansion-panel-text class="reports-filter-content">
                <v-checkbox
                  v-for="option in getSortedOptions(filter)"
                  :key="option.value"
                  :model-value="
                    option.value === 'any'
                      ? isAnySelected(filter)
                      : isOptionSelected(filter, option)
                  "
                  :label="option.label"
                  density="compact"
                  color="primary"
                  hide-details
                  class="bordered-checkbox"
                  @update:model-value="
                    (val: boolean | null) => {
                      if (option.value === 'any') {
                        handleAnySelection(filter, val ?? false);
                      } else {
                        handleFilterInteraction(
                          filter.id,
                          option.value,
                          val ?? false,
                        );
                      }
                    }
                  "
                ></v-checkbox>
                <!-- Dynamic filter description with HTML support -->
                <div
                  v-if="filter.description"
                  class="filter-description pt-2"
                  v-html="filter.description"
                ></div>
              </v-expansion-panel-text>
            </v-expansion-panel>
            <v-divider
              v-if="index < reportsStore.filters.length - 1"
            ></v-divider>
          </template>
        </template>
        <template v-else>
          <div
            v-if="!isLoading && !reportsStore.error && !hasFilters"
            class="text-body-2 pa-4"
          >
            No filters available
          </div>
          <div v-else-if="reportsStore.error" class="text-error pa-4">
            {{ reportsStore.error }}
          </div>
          <div v-else class="pa-4 text-grey">Loading filters...</div>
        </template>
      </v-expansion-panels>
    </template>
  </div>
</template>

<script setup lang="ts">
  import { ref, computed, watch, onMounted, nextTick } from 'vue';
  import { useGlossaryStore, useReportsStore } from '../../store';
  import { useDisplay } from 'vuetify';
  import { useRouter, useRoute } from 'vue-router';
  import { registerGlossaryLinksInContainer } from '../../composables/useGlossaryLinks';
  import type {
    FilterGroup,
    FilterOption,
  } from '../../store/modules/reportsStore';

  const useDebounceFn = (fn: (...args: any[]) => void, delay: number) => {
    let timeout: number | null = null;
    return (...args: any[]) => {
      if (timeout !== null) clearTimeout(timeout);
      timeout = window.setTimeout(() => {
        fn(...args);
        timeout = null;
      }, delay);
    };
  };

  const display = useDisplay();
  const mdAndUp = computed(() => display.mdAndUp.value);
  const mobileExpandedPanel = ref<number | null>(null);
  const expandedPanels = ref<number[]>([]);
  const router = useRouter();
  const route = useRoute();
  const glossaryStore = useGlossaryStore();
  const reportsStore = useReportsStore();

  const findFilterIndex = (filterId: string) =>
    reportsStore.filters.findIndex((filter) => filter.id === filterId);

  const expandSubjectPanel = () => {
    const subjectIndex = findFilterIndex('subject');
    if (subjectIndex !== -1 && !expandedPanels.value.includes(subjectIndex)) {
      expandedPanels.value = [...expandedPanels.value, subjectIndex];
    }
  };

  watch(
    () => route.params.subject,
    (newSubject) => {
      if (newSubject) nextTick(() => expandSubjectPanel());
    },
    { immediate: true },
  );

  const updateRoute = useDebounceFn(() => {
    const query: Record<string, string[]> = {};
    const subject = reportsStore.selectedSubjects[0];
    if (reportsStore.selectedFormats.length > 0)
      query.formats = reportsStore.selectedFormats;
    if (reportsStore.selectedGrades.length > 0)
      query.grades = reportsStore.selectedGrades;
    if (reportsStore.selectedAlignment.length > 0)
      query.alignment = reportsStore.selectedAlignment;
    if (reportsStore.selectedUsability.length > 0)
      query.usability = reportsStore.selectedUsability;
    if (reportsStore.selectedVersions.length > 0)
      query.versions = reportsStore.selectedVersions;
    if (
      reportsStore.selectedPublishers &&
      reportsStore.selectedPublishers.length > 0
    )
      query.publishers = reportsStore.selectedPublishers;
    if (
      reportsStore.selectedSupportingDocuments &&
      reportsStore.selectedSupportingDocuments.length > 0
    )
      query.supportingDocuments = reportsStore.selectedSupportingDocuments;
    const path = subject ? `/reports/${subject.toLowerCase()}` : '/reports';
    router.replace({ path, query });
  }, 300);

  const optionSelectedCache = new Map();
  function isOptionSelected(
    filter: FilterGroup,
    option: FilterOption,
  ): boolean {
    const cacheKey = `${filter.id}-${option.value}`;
    if (optionSelectedCache.has(cacheKey)) {
      return optionSelectedCache.get(cacheKey);
    }

    let result = false;
    const apiSlug = filter.slug || filter.id;

    switch (apiSlug) {
      case 'subject':
        const subjectValue = option.value.toUpperCase();
        result = reportsStore.selectedSubjects.some(
          (subj) => subj.toUpperCase() === subjectValue,
        );
        break;
      case 'formats':
        result = reportsStore.selectedFormats.includes(option.value);
        break;
      case 'grade-levels':
        result = reportsStore.selectedGrades.includes(option.value);
        break;
      case 'versions':
        result = reportsStore.selectedVersions.includes(option.value);
        break;
      case 'alignment':
        result = reportsStore.selectedAlignment.includes(option.value);
        break;
      case 'usability':
        result = reportsStore.selectedUsability.includes(option.value);
        break;
      case 'publishers':
        result =
          reportsStore.selectedPublishers?.includes(option.value) || false;
        break;
      case 'supporting-documents':
        result =
          reportsStore.selectedSupportingDocuments?.includes(option.value) ||
          false;
        break;
      default:
        // Handle any other filter types that might be added
        const filterStateKey = `selected${apiSlug.charAt(0).toUpperCase() + apiSlug.slice(1).replace(/-([a-z])/g, (_, letter) => letter.toUpperCase())}s`;
        const filterState = (reportsStore as any)[filterStateKey];
        if (Array.isArray(filterState)) {
          result = filterState.includes(option.value);
        }
        break;
    }
    optionSelectedCache.set(cacheKey, result);
    return result;
  }

  async function handleFilterInteraction(
    filterIdFromComponent: string,
    value: string,
    selected: boolean,
  ) {
    const filterDefinition = reportsStore.filters.find(
      (f) => f.id === filterIdFromComponent || f.slug === filterIdFromComponent,
    );
    const apiSlug = filterDefinition?.slug || filterIdFromComponent;

    switch (apiSlug) {
      case 'subject':
        reportsStore.setSubject(value, selected);
        break;
      case 'formats':
        reportsStore.setFormat(value, selected);
        break;
      case 'grade-levels':
        reportsStore.setGrade(value, selected);
        break;
      case 'versions':
        reportsStore.setVersion(value, selected);
        break;
      case 'alignment':
        reportsStore.setAlignment(value, selected);
        break;
      case 'usability':
        reportsStore.setUsability(value, selected);
        break;
      case 'publishers':
        reportsStore.setPublisher?.(value, selected);
        break;
      case 'supporting-documents':
        reportsStore.setSupportingDocument?.(value, selected);
        break;
      default:
        // Handle any other filter types dynamically
        const methodName = `set${apiSlug.charAt(0).toUpperCase() + apiSlug.slice(1).replace(/-([a-z])/g, (_, letter) => letter.toUpperCase())}`;
        if (typeof (reportsStore as any)[methodName] === 'function') {
          (reportsStore as any)[methodName](value, selected);
        }
        break;
    }

    await nextTick();

    try {
      await reportsStore.fetchFilters();
      // Fetch reports with the updated filters
      await reportsStore.fetchReports();
    } catch (error) {
      console.error(
        '[ReportsAllFilters] Error during reportsStore.fetchFilters():',
        error,
      );
    }

    await nextTick();

    optionSelectedCache.clear();
    updateRoute();
  }

  watch(
    [
      () => [...reportsStore.selectedSubjects],
      () => [...reportsStore.selectedFormats],
      () => [...reportsStore.selectedGrades],
      () => [...reportsStore.selectedVersions],
      () => [...reportsStore.selectedAlignment],
      () => [...reportsStore.selectedUsability],
      () => [...(reportsStore.selectedPublishers || [])],
      () => [...(reportsStore.selectedSupportingDocuments || [])],
      () => reportsStore.additionalFilters.size,
    ],
    () => {
      optionSelectedCache.clear();
      updateRoute();
    },
    { deep: true },
  );

  // Enhanced glossary links when filters update
  watch(
    () => reportsStore.filters,
    () => {
      nextTick(() => {
        const container = document.querySelector('.reports-filters-container');
        if (container) {
          registerGlossaryLinksInContainer(container as HTMLElement);
        }
      });
    },
    { deep: true },
  );

  // Initialize glossary links on mount
  onMounted(() => {
    nextTick(() => {
      const container = document.querySelector('.reports-filters-container');
      if (container) {
        registerGlossaryLinksInContainer(container as HTMLElement);
      }
    });
  });

  const hasFilters = computed(
    () =>
      Array.isArray(reportsStore.filters) && reportsStore.filters.length > 0,
  );
  const isLoading = computed(() => reportsStore.loading);

  // Add computed property for sorted filter options
  const getSortedOptions = (filter: FilterGroup) => {
    let options = filter.options;

    if (filter.id === 'subject') {
      options = [...filter.options].sort((a, b) =>
        a.label.localeCompare(b.label),
      );
    }

    if (filter.id === 'versions' || filter.slug === 'versions') {
      options = [...filter.options].sort((a, b) => {
        // Extract version numbers for proper numeric sorting
        const getVersionNumber = (version: string) => {
          const match = version.match(/v?(\d+\.?\d*)/);
          return match ? parseFloat(match[1]) : 0;
        };

        const versionA = getVersionNumber(a.label);
        const versionB = getVersionNumber(b.label);

        // Sort in descending order (highest version first)
        return versionB - versionA;
      });
    }

    // Add "Any" option for multiple select filters
    if (filter.multiple && options.length > 1) {
      const anyOption: FilterOption = {
        value: 'any',
        label: 'Any',
        name: 'Any',
      };
      options = [anyOption, ...options];
    }

    return options;
  };

  // Check if "Any" is selected (no other options selected)
  const isAnySelected = (filter: FilterGroup): boolean => {
    const apiSlug = filter.slug || filter.id;

    switch (apiSlug) {
      case 'subject':
        return reportsStore.selectedSubjects.length === 0;
      case 'formats':
        return reportsStore.selectedFormats.length === 0;
      case 'grade-levels':
        return reportsStore.selectedGrades.length === 0;
      case 'versions':
        return reportsStore.selectedVersions.length === 0;
      case 'alignment':
        return reportsStore.selectedAlignment.length === 0;
      case 'usability':
        return reportsStore.selectedUsability.length === 0;
      case 'publishers':
        return (reportsStore.selectedPublishers?.length || 0) === 0;
      case 'supporting-documents':
        return (reportsStore.selectedSupportingDocuments?.length || 0) === 0;
      default:
        const filterStateKey = `selected${apiSlug.charAt(0).toUpperCase() + apiSlug.slice(1).replace(/-([a-z])/g, (_, letter) => letter.toUpperCase())}s`;
        const filterState = (reportsStore as any)[filterStateKey];
        return Array.isArray(filterState) ? filterState.length === 0 : true;
    }
  };

  // Handle "Any" option selection
  const handleAnySelection = (filter: FilterGroup, selected: boolean) => {
    if (selected) {
      // Clear all other selections for this filter
      const apiSlug = filter.slug || filter.id;
      switch (apiSlug) {
        case 'subject':
          reportsStore.selectedSubjects.length = 0;
          break;
        case 'formats':
          reportsStore.selectedFormats.length = 0;
          break;
        case 'grade-levels':
          reportsStore.selectedGrades.length = 0;
          break;
        case 'versions':
          reportsStore.selectedVersions.length = 0;
          break;
        case 'alignment':
          reportsStore.selectedAlignment.length = 0;
          break;
        case 'usability':
          reportsStore.selectedUsability.length = 0;
          break;
        case 'publishers':
          if (reportsStore.selectedPublishers) {
            reportsStore.selectedPublishers.length = 0;
          }
          break;
        case 'supporting-documents':
          if (reportsStore.selectedSupportingDocuments) {
            reportsStore.selectedSupportingDocuments.length = 0;
          }
          break;
        default:
          const filterStateKey = `selected${apiSlug.charAt(0).toUpperCase() + apiSlug.slice(1).replace(/-([a-z])/g, (_, letter) => letter.toUpperCase())}s`;
          const filterState = (reportsStore as any)[filterStateKey];
          if (Array.isArray(filterState)) {
            filterState.length = 0;
          }
          break;
      }

      // Trigger filter update
      reportsStore.fetchFilters();
      reportsStore.fetchReports();
      updateRoute();
    }
  };
</script>

<style lang="scss" scoped>
  .reports-filters-container {
    .v-divider {
      margin: 0 !important;
      border-color: rgba(0, 0, 0, 0.12) !important;
      opacity: 1 !important;
    }
    .v3-reports-sidebar.reports-sidebar-container &,
    .reports-sidebar-container &,
    & {
      .reports-filter-section {
        border-bottom: 1px solid rgba(0, 0, 0, 0.12) !important;
        contain: content !important;
      }

      .section-title {
        font-size: 14px !important;
        line-height: 1.4 !important;
        font-weight: 400 !important;
      }

      .reports-filter-content {
        padding-bottom: 16px !important;
        contain: content !important;

        :deep(.v-checkbox) {
          .v-label {
            font-size: 13px !important;
          }

          @media (max-width: 768px) {
            .v-selection-control {
              min-height: 40px !important;
              touch-action: manipulation !important;
            }
          }
        }
      }

      .reports-filter-panels {
        background: transparent !important;
        box-shadow: none !important;
        contain: layout style !important;

        :deep(.v-expansion-panel) {
          background: transparent !important;
          box-shadow: none !important;
          border: none !important;
          contain: content !important;

          .v-expansion-panel-title__icon {
            color: rgb(var(--v-theme-primary)) !important;

            svg {
              width: 18px !important;
              height: 18px !important;
              min-width: 18px !important;
              min-height: 18px !important;
              max-width: 18px !important;
              max-height: 18px !important;
              display: block !important;
            }
          }
        }

        :deep(.v-expansion-panel-title) {
          min-height: unset !important;
          border: none !important;
          touch-action: manipulation !important;
        }

        :deep(.v-expansion-panel-title__overlay) {
          display: none !important;
        }

        :deep(.v-expansion-panel-title--active) {
          min-height: unset !important;
          border: none !important;
        }

        :deep(.v-expansion-panel-text__wrapper) {
          padding: 0 !important;
        }

        :deep(.v-expansion-panel::after) {
          border: none !important;
        }
      }

      .reset-link {
        cursor: pointer !important;
        text-decoration: underline !important;
        margin-top: 0 !important;
        display: inline-block !important;
        touch-action: manipulation !important;
        padding: 8px 0 !important;
      }

      .bordered-checkbox {
        :deep(.v-selection-control) {
          min-height: 30px !important;
        }

        :deep(.v-checkbox .v-selection-control__input) {
          width: 20px !important;
          height: 20px !important;
        }

        :deep(.v-selection-control__wrapper) {
          width: 20px !important;
          height: 20px !important;
        }

        :deep(.v-label) {
          margin-left: 8px !important;
          color: rgb(var(--v-theme-secondary)) !important;
          font-size: 13px !important;
          line-height: 1.2 !important;
        }

        :deep(.v-input__details) {
          display: none !important;
        }
      }

      .filter-description {
        font-size: 13px !important;
        color: rgb(var(--v-theme-secondary)) !important;
        line-height: 1.4 !important;

        :deep(.glossary-link) {
          color: rgb(var(--v-theme-primary)) !important;
          text-decoration: underline !important;
          cursor: pointer !important;
          transition: all 0.2s ease !important;

          &:hover {
            color: rgb(var(--v-theme-primary-darken-1)) !important;
            text-decoration: none !important;
          }

          &.glossary-link-active {
            background-color: rgb(var(--v-theme-primary-container)) !important;
            padding: 2px 4px !important;
            border-radius: 4px !important;
          }

          &.glossary-link-loading {
            opacity: 0.6 !important;
            pointer-events: none !important;
          }
        }
      }
    }
  }

  // Additional overrides for production environment conflicts
  .reports-filters-container {
    box-sizing: border-box !important;
    width: 100% !important;

    * {
      box-sizing: border-box !important;
    }
  }
</style>
